import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ExternalLink, FolderGit2, KeyRound, Link2Off, MessageSquareText, Copy, PenLine, type LucideIcon } from "lucide-react";
import type { FogbugzToAdoRefusal, FogbugzToAdoRefusalCode } from "@ttc-spectree/toolbox-contract";
import type { FormulatorPrefill } from "@/components/formulator/formulator-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { canAnywhere, canOn, projectsWith, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import type { ToolboxAccessResponse } from "@ttc-spectree/toolbox-contract/roles";
import { projectPath } from "@/lib/grooming/paths";
import { adoSetupRedirect } from "@/lib/toolbox/page-names";
import { FORMULATOR_PATH, FOGBUGZ_TO_ADO_PATH } from "@/lib/toolbox/tools";

/**
 * The case step of FogBugz to ADO (epic PI-SPEC-SW-E311, tasks
 * PI-SPEC-SW-1640-2 and PI-SPEC-SW-1640-5): the case number box, and when
 * SpecTree refuses the case, one plain card with the next step. A refusal
 * ends the flow: no Claude run starts and nothing is written.
 */

/** Digits only, as typed into the case number box. Pure; exported for tests. */
export function caseNumberDigits(value: string): string {
  return value.replace(/\D+/g, "");
}

export interface CaseStepProps {
  /** The PM loads a case: the page prepares it. */
  onLoad: (caseId: number) => void;
  /** True while a case is being prepared or Claude runs; the box stays, Load waits. */
  busy: boolean;
}

/** The case number box. */
export function CaseStep({ onLoad, busy }: CaseStepProps) {
  const [caseInput, setCaseInput] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const digits = caseNumberDigits(caseInput);
    if (!digits || busy) return;
    onLoad(Number(digits));
  };
  return (
    <form className="flex items-end gap-2" onSubmit={submit} data-testid="fogbugz-to-ado-case-form">
      <label className="flex flex-col gap-1 text-[13px] font-medium" htmlFor="fogbugz-to-ado-case-number">
        FogBugz case number
        <Input
          id="fogbugz-to-ado-case-number"
          inputMode="numeric"
          autoComplete="off"
          className="w-40"
          value={caseInput}
          onChange={(event) => {
            setCaseInput(caseNumberDigits(event.target.value));
          }}
        />
      </label>
      <Button type="submit" disabled={!caseInput || busy}>
        Check the case
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

const HEADINGS: Record<FogbugzToAdoRefusalCode, { heading: string; icon: LucideIcon }> = {
  FOGBUGZ_KEY_MISSING: { heading: "Save your FogBugz key", icon: KeyRound },
  ADO_PAT_MISSING: { heading: "Connect Azure DevOps", icon: KeyRound },
  NOT_A_BUG_CASE: { heading: "This case is not a bug", icon: MessageSquareText },
  FOGBUGZ_PROJECT_NOT_LINKED: { heading: "This FogBugz project is not linked to an ADO project", icon: Link2Off },
  REPOS_NOT_SET_UP: { heading: "The product has no code set up", icon: FolderGit2 },
  DUPLICATE_FOUND: { heading: "This case is already on the ADO board", icon: Copy },
};

/**
 * The ADO project named in `message` that the person may set up, or
 * undefined: the longest of their projects with `capability` that the
 * sentence mentions (the refusal names the project in words only). Pure;
 * exported for tests.
 */
export function projectNamedIn(
  message: string,
  access: ToolboxAccessResponse | null | undefined,
  capability: "map-project" | "manage-repos",
): string | undefined {
  if (!access) return undefined;
  const lower = message.toLowerCase();
  return access.projects
    .map((entry) => entry.container)
    .filter((project) => lower.includes(project.toLowerCase()) && canOn(access, project, capability))
    .sort((a, b) => b.length - a.length)[0];
}

export interface RefusalCardProps {
  caseId: number;
  refusal: FogbugzToAdoRefusal;
}

/**
 * One refusal as a plain card: SpecTree's sentence and the right next step.
 * - DUPLICATE_FOUND: "Open ADO Bug #N" and "Nothing was written."
 * - FOGBUGZ_PROJECT_NOT_LINKED: the Project page's FogBugz tab (a PM or Admin links it there).
 * - REPOS_NOT_SET_UP: the Project page's Repositories tab of the named project.
 * - NOT_A_BUG_CASE: a Feature case's "Open in ADO Formulator" (a User Story with the case
 *   text, for a person who may use the ADO Formulator); anything else the message only.
 * - FOGBUGZ_KEY_MISSING: Settings. ADO_PAT_MISSING: Setup's "Connect Azure DevOps" step.
 */
export function RefusalCard({ caseId, refusal }: RefusalCardProps) {
  const navigate = useNavigate();
  const { data: access } = useToolboxAccess();
  const { heading, icon: Icon } = HEADINGS[refusal.code];
  const linkProject = refusal.code === "FOGBUGZ_PROJECT_NOT_LINKED" ? projectsWith(access, "map-project")[0] : undefined;
  const reposProject =
    refusal.code === "REPOS_NOT_SET_UP"
      ? (projectNamedIn(refusal.message, access, "manage-repos") ?? projectNamedIn(refusal.message, access, "map-project"))
      : undefined;
  const handoff = refusal.code === "NOT_A_BUG_CASE" ? refusal.handoff : undefined;
  const mayFormulate = canAnywhere(access, "formulator");

  const openFormulator = () => {
    if (!handoff) return;
    const prefill: FormulatorPrefill = {
      kind: handoff.kind,
      ...(handoff.adoProject ? { project: handoff.adoProject } : {}),
      description: handoff.description,
    };
    void navigate(FORMULATOR_PATH, { state: { prefill } });
  };

  return (
    <div
      className="glass-card flex items-start gap-3 rounded-[10px] px-[18px] py-4"
      role="alert"
      data-testid={`fogbugz-to-ado-refusal-${refusal.code}`}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#1A1A1E]" aria-hidden="true">
        <Icon className="h-4 w-4 text-amber-400" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <h2 className="font-display text-base font-semibold leading-snug">{heading}</h2>
        <p className="leading-relaxed text-muted-foreground" data-testid="fogbugz-to-ado-refusal-message">
          {refusal.message}
        </p>
        {refusal.code === "DUPLICATE_FOUND" && (
          <p className="text-[13px] text-muted-foreground">Nothing was written to ADO or to case #{caseId}.</p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {refusal.code === "DUPLICATE_FOUND" && refusal.existing && (
            <Button asChild size="sm" variant="outline">
              <a href={refusal.existing.url} target="_blank" rel="noreferrer" data-testid="fogbugz-to-ado-existing-link">
                <ExternalLink aria-hidden="true" />
                Open ADO Bug #{refusal.existing.id}
              </a>
            </Button>
          )}
          {linkProject && (
            <Button asChild size="sm" variant="outline">
              <Link to={projectPath(linkProject, "fogbugz")} data-testid="fogbugz-to-ado-project-link">
                Open the Project page
              </Link>
            </Button>
          )}
          {reposProject && (
            <Button asChild size="sm" variant="outline">
              <Link to={projectPath(reposProject, "repositories")} data-testid="fogbugz-to-ado-repos-link">
                Open the Repositories page
              </Link>
            </Button>
          )}
          {refusal.code === "FOGBUGZ_KEY_MISSING" && (
            <Button asChild size="sm" variant="outline">
              <Link to="/settings">Open Settings</Link>
            </Button>
          )}
          {refusal.code === "ADO_PAT_MISSING" && (
            <Button asChild size="sm" variant="outline">
              <Link to={adoSetupRedirect(FOGBUGZ_TO_ADO_PATH)}>Connect Azure DevOps</Link>
            </Button>
          )}
          {handoff && mayFormulate && (
            <Button type="button" size="sm" onClick={openFormulator} data-testid="fogbugz-to-ado-open-formulator">
              <PenLine aria-hidden="true" />
              Open in ADO Formulator
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
